import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableSortLabel, Paper, Chip, CircularProgress, Alert, TextField,
  ToggleButtonGroup, ToggleButton, LinearProgress, Tooltip, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider,
} from "@mui/material";
import { API_URL } from "../config";
import PageHeader from "../components/PageHeader";

const SUBJECT_LABEL = { DS: "DS学", MD1: "MD1", MD2: "MD2", SI: "SI(ソーシャルイノベーション)" };

// KPIタイル: 危険度を一目で伝える
function StatTile({ label, value, color, hint }) {
  return (
    <Paper sx={{ p: 1.5, minWidth: 130, flex: "1 1 130px",
      borderLeft: `4px solid ${color}` }}>
      <Typography variant="h4" sx={{ fontWeight: "bold", color, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>{label}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Paper>
  );
}

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// 提出率で色分け（低いほど危険）
function rateColor(rate) {
  if (rate == null) return "default";
  if (rate < 40) return "error";
  if (rate < 70) return "warning";
  return "success";
}

const SUBJECTS = ["DS", "MD1", "MD2", "SI"];

export default function AdminAtRisk() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [program, setProgram] = useState("all");
  const [q, setQ] = useState("");
  // 既定は null = API のリスク順（未参加>D評価>未提出）を保持。列クリックでソート。
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState("desc");
  const [detail, setDetail] = useState(null);   // 行クリックで開く学生詳細

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [s, r] = await Promise.all([
          axios.get(`${API_URL}/admin/at-risk/summary`, authHeader()),
          axios.get(`${API_URL}/admin/at-risk/students`, authHeader()),
        ]);
        setSummary(s.data);
        setRows(r.data.students);
      } catch (e) {
        setError(e?.response?.data?.detail || "データ取得に失敗しました");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSort = (key) => {
    if (orderBy === key) setOrder(order === "asc" ? "desc" : "asc");
    else { setOrderBy(key); setOrder("desc"); }
  };

  const filtered = useMemo(() => {
    let list = rows;
    if (program !== "all") list = list.filter((r) => r.program === program);
    if (q.trim()) {
      const k = q.trim().toLowerCase();
      list = list.filter(
        (r) => r.student_id.includes(k) || (r.name || "").toLowerCase().includes(k)
      );
    }
    if (!orderBy) return list;   // API のリスク順を維持
    const val = (r) => {
      if (orderBy === "not_in_classroom") return r.not_in_classroom?.length || 0;
      if (orderBy === "d_risk") return r.d_risk ? 1 : 0;
      return r[orderBy];
    };
    const cmp = (a, b) => {
      const av = val(a), bv = val(b);
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return order === "asc" ? -1 : 1;
      if (av > bv) return order === "asc" ? 1 : -1;
      return 0;
    };
    return [...list].sort(cmp);
  }, [rows, program, q, orderBy, order]);

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress color="success" />
      </Box>
    );
  }

  // KPI集計（危険度の高い順に把握できる指標）
  const kpi = {
    total: rows.length,
    absent: rows.filter((r) => r.not_in_classroom?.length > 0).length,
    dRisk: rows.filter((r) => r.d_risk).length,
    low: rows.filter((r) => r.submit_rate != null && r.submit_rate < 40).length,
  };

  return (
    <Box sx={{ p: 2 }}>
      <PageHeader
        title="留年リスク早期発見"
        subtitle="危険度の高い順に自動表示（Classroom未参加 ＞ D評価リスク ＞ 未提出）。行をクリックで詳細。"
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* 一目で分かるKPI */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
        <StatTile label="対象学生" value={kpi.total} color="#90a4ae" />
        <StatTile label="Classroom未参加" value={kpi.absent} color="#e53935"
          hint="最優先で対応" />
        <StatTile label="D評価リスク" value={kpi.dRisk} color="#fb8c00"
          hint="MD標準課題1/3以下" />
        <StatTile label="低提出(40%未満)" value={kpi.low} color="#fbc02d" />
      </Box>

      {summary && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            最終同期: {summary.last_synced_at
              ? new Date(summary.last_synced_at).toLocaleString("ja-JP")
              : "未同期"}
          </Typography>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1 }}>
            {summary.by_subject.map((s) => {
              const rate = s.total ? Math.round((s.turned / s.total) * 100) : 0;
              return (
                <Box key={s.subject} sx={{ minWidth: 160 }}>
                  <Typography variant="subtitle2">
                    {s.subject}（{s.students}名）
                  </Typography>
                  <Typography variant="body2">
                    提出 {s.turned}/{s.total}・未提出 {s.missing}・遅延 {s.late}
                  </Typography>
                  <LinearProgress
                    variant="determinate" value={rate}
                    color={rate < 40 ? "error" : rate < 70 ? "warning" : "success"}
                    sx={{ mt: 0.5, height: 8, borderRadius: 1 }}
                  />
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      <Box sx={{ display: "flex", gap: 2, mb: 1, alignItems: "center", flexWrap: "wrap" }}>
        <ToggleButtonGroup
          size="small" value={program} exclusive
          onChange={(_, v) => v && setProgram(v)}
        >
          <ToggleButton value="all">全体</ToggleButton>
          <ToggleButton value="通学">通学</ToggleButton>
          <ToggleButton value="通信">通信(MIDS)</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small" label="学籍番号・氏名で検索"
          value={q} onChange={(e) => setQ(e.target.value)}
        />
        <Typography variant="body2" color="text.secondary">
          {filtered.length} 名
        </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: "70vh" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {[
                ["student_id", "学籍番号"], ["name", "氏名"], ["program", "区分"],
                ["not_in_classroom", "Classroom"],
                ["d_risk", "D評価"],
                ["submit_rate", "提出率(標準)"], ["std_turned", "提出数"],
                ["missing", "未提出"], ["late", "遅延"],
              ].map(([key, label]) => (
                <TableCell key={key} sortDirection={orderBy === key ? order : false}>
                  <TableSortLabel
                    active={orderBy === key}
                    direction={orderBy === key ? order : "asc"}
                    onClick={() => handleSort(key)}
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
              {SUBJECTS.map((s) => <TableCell key={s} align="center">{s}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.student_id} hover sx={{ cursor: "pointer" }}
                onClick={() => setDetail(r)}>
                <TableCell>{r.student_id}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell>
                  <Chip
                    size="small" label={r.program}
                    color={r.program === "通信" ? "info" : "default"}
                  />
                </TableCell>
                <TableCell>
                  {r.not_in_classroom?.length > 0 ? (
                    <Tooltip title={`名簿にいるがClassroom未参加: ${r.not_in_classroom.join("・")}`}>
                      <Chip size="small" color="error" label={`未参加 ${r.not_in_classroom.join("/")}`} />
                    </Tooltip>
                  ) : (
                    <Chip size="small" variant="outlined" color="success" label="OK" />
                  )}
                </TableCell>
                <TableCell>
                  {r.d_risk ? (
                    <Tooltip title={`標準課題の提出が1/3以下: ${r.d_risk_subjects.join("・")}`}>
                      <Chip size="small" color="error" label={`D ${r.d_risk_subjects.join("/")}`} />
                    </Tooltip>
                  ) : (
                    <Chip size="small" variant="outlined" color="success" label="—" />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={r.submit_rate == null ? "—" : `${r.submit_rate}%`}
                    color={rateColor(r.submit_rate)}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={`標準課題 ${r.std_turned}/${r.std_total}・発展含む全体 ${r.turned_in}/${r.total}`}>
                    <span>{r.std_turned}/{r.std_total}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{r.missing}</TableCell>
                <TableCell>{r.late}</TableCell>
                {SUBJECTS.map((s) => {
                  const d = r.by_subject?.[s];
                  if (!r.enrolled?.[s]) {
                    return <TableCell key={s} align="center" sx={{ color: "text.disabled" }}>-</TableCell>;
                  }
                  if (!d) {
                    return <TableCell key={s} align="center" sx={{ color: "error.main", fontWeight: "bold" }}>未参加</TableCell>;
                  }
                  const isMD = s === "MD1" || s === "MD2";
                  // MDは標準課題ベースで表示（発展は任意のため）
                  const shown = isMD ? `${d.std_turned}/${d.std_total}` : `${d.turned_in}/${d.total}`;
                  const dRiskHere = r.d_risk_subjects?.includes(s);
                  return (
                    <TableCell key={s} align="center">
                      <Tooltip title={
                        isMD
                          ? `標準 ${d.std_turned}/${d.std_total}・全体(発展含む) ${d.turned_in}/${d.total}・遅延 ${d.late}`
                          : `提出 ${d.turned_in}/${d.total}・遅延 ${d.late}`
                      }>
                        <span style={{
                          color: dRiskHere ? "#e53935" : d.missing > 0 ? "#ff7043" : "inherit",
                          fontWeight: dRiskHere ? "bold" : "normal",
                        }}>
                          {shown}
                        </span>
                      </Tooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        {detail && (
          <>
            <DialogTitle>
              {detail.name}（{detail.student_id}）
              <Chip size="small" label={detail.program} sx={{ ml: 1 }}
                color={detail.program === "通信" ? "info" : "default"} />
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {detail.email}・{detail.grade_year}
              </Typography>
              <Typography variant="subtitle1" sx={{ mt: 1 }}>
                標準課題 提出率 {detail.submit_rate == null ? "—" : `${detail.submit_rate}%`}
                （標準 {detail.std_turned}/{detail.std_total}・遅延 {detail.late}）
              </Typography>
              {detail.not_in_classroom?.length > 0 && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  🚨 Classroom未参加: {detail.not_in_classroom.join("・")}
                  （名簿にいるが授業に入っていません。最優先で対応してください）
                </Alert>
              )}
              {detail.d_risk && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  D評価リスク: {detail.d_risk_subjects.join("・")}（標準課題の提出が1/3以下）
                </Alert>
              )}
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" gutterBottom>
                科目別（MDは標準課題ベース。括弧内は発展課題含む全体）
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>科目</TableCell><TableCell align="center">標準 提出</TableCell>
                    <TableCell align="center">未提出</TableCell><TableCell align="center">遅延</TableCell>
                    <TableCell align="center">提出率</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {["DS", "MD1", "MD2", "SI"].map((s) => {
                    if (!detail.enrolled?.[s]) return null;
                    const d = detail.by_subject?.[s];
                    const isMD = s === "MD1" || s === "MD2";
                    const num = d ? (isMD ? d.std_turned : d.turned_in) : 0;
                    const den = d ? (isMD ? d.std_total : d.total) : 0;
                    const rate = den ? Math.round((num / den) * 100) : null;
                    const dRiskHere = detail.d_risk_subjects?.includes(s);
                    return (
                      <TableRow key={s}>
                        <TableCell>
                          {SUBJECT_LABEL[s]}
                          {dRiskHere && <Chip size="small" color="error" label="D" sx={{ ml: 0.5 }} />}
                        </TableCell>
                        <TableCell align="center">
                          {!d ? (
                            <span style={{ color: "#e53935", fontWeight: "bold" }}>未参加</span>
                          ) : (
                            <>
                              {num}/{den}
                              {isMD && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                  全体 {d.turned_in}/{d.total}
                                </Typography>
                              )}
                            </>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ color: d && d.missing > 0 ? "#ff7043" : "inherit" }}>
                          {d ? d.missing : "-"}
                        </TableCell>
                        <TableCell align="center">{d ? d.late : "-"}</TableCell>
                        <TableCell align="center">
                          {rate == null ? "—" : (
                            <Chip size="small" label={`${rate}%`} color={rateColor(rate)} />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetail(null)}>閉じる</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
